import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  Visibility,
  Cancel,
  FilterList,
  Receipt,
  CheckCircle
} from '@mui/icons-material';
import saleService from '../services/saleService';
import { useAuth } from '../context/AuthContext';
import InvoiceModal from '../components/InvoiceModal';
import ReceiptModal from '../components/ReceiptModal';
import useInvoice from '../hooks/useInvoice';
import AdminPageHeader from '../components/AdminPageHeader';

const Sales = () => {
  const { isAdmin } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    paymentMethod: ''
  });
  
  // Estados para facturación
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const { getInvoiceBySale } = useInvoice();
  
  // Estados para recibo
  const [openReceipt, setOpenReceipt] = useState(false);

  useEffect(() => {
    loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSales = async () => {
    setLoading(true);
    const params = {
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      paymentMethod: filters.paymentMethod || undefined
    };
    const result = await saleService.getAll(params);
    if (result.success) {
      setSales(result.data);
    }
    setLoading(false);
  };

  const handleViewDetail = async (saleId) => {
    const result = await saleService.getById(saleId);
    if (result.success) {
      setSelectedSale(result.data);
      setOpenDetail(true);
      // Cargar factura si existe
      loadInvoiceForSale(saleId);
    }
  };

  const loadInvoiceForSale = async (saleId) => {
    const invoiceResult = await getInvoiceBySale(saleId);
    if (invoiceResult?.success) {
      setCurrentInvoice(invoiceResult.data);
    } else {
      setCurrentInvoice(null);
    }
  };

  const handleOpenInvoiceModal = () => {
    setOpenInvoiceModal(true);
  };

  const handleInvoiceSuccess = (invoiceData) => {
    setCurrentInvoice({
      invoice_type: invoiceData.invoice_type,
      invoice_number: invoiceData.invoice_number,
      cae: invoiceData.cae,
      cae_expiration: invoiceData.cae_expiration
    });
    setMessage({ type: 'success', text: 'Factura generada exitosamente' });
  };

  const handleCancelSale = async (saleId) => {
    if (window.confirm('¿Estás seguro de cancelar esta venta? El stock se devolverá.')) {
      const result = await saleService.cancel(saleId);
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        loadSales();
        setOpenDetail(false);
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      efectivo: 'Efectivo',
      tarjeta: 'Tarjeta',
      transferencia: 'Transferencia',
      mercadopago: 'MercadoPago'
    };
    return labels[method] || method;
  };

  const getStatusColor = (status) => {
    const colors = {
      pagado: 'success',
      pendiente: 'warning',
      cancelado: 'error'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box className="d-flex justify-center align-center min-h-60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="admin-page">
      <AdminPageHeader
        eyebrow="OPERACIONES"
        title="Historial de ventas"
        subtitle="Consultá comprobantes, importes y el detalle de cada operación."
        icon={<Receipt />}
      />

      {message.text && (
        <Alert severity={message.type} onClose={() => setMessage({ type: '', text: '' })} className="mb-md">
          {message.text}
        </Alert>
      )}

      {/* Filtros */}
      <Card className="mb-lg">
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Desde"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Hasta"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Método de Pago"
                value={filters.paymentMethod}
                onChange={(e) => setFilters({ ...filters, paymentMethod: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Todos</option>
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="transferencia">Transferencia</option>
                <option value="mercadopago">MercadoPago</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<FilterList />}
                onClick={loadSales}
              >
                Filtrar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabla de ventas */}
      <Card>
        <CardContent>
          <Box className="table-container">
            <table className="table table-responsive">
              <thead>
                <tr>
                  <th>N° Venta</th>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>Vendedor</th>
                  <th>Método de Pago</th>
                  <th className="text-right">Total</th>
                  <th className="text-center">Estado</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sales.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No hay ventas para mostrar
                    </td>
                  </tr>
                ) : (
                  sales.map((sale) => (
                    <tr key={sale.id}>
                      <td className="font-semibold">{sale.saleNumber}</td>
                      <td>{new Date(sale.saleDate).toLocaleDateString('es-AR')}</td>
                      <td>{sale.customerName || 'Cliente General'}</td>
                      <td>{sale.user?.fullName}</td>
                      <td>{getPaymentMethodLabel(sale.paymentMethod)}</td>
                      <td className="text-right font-semibold">
                        ${sale.total.toLocaleString('es-AR')}
                      </td>
                      <td className="text-center">
                        <Chip
                          label={sale.paymentStatus}
                          color={getStatusColor(sale.paymentStatus)}
                          size="small"
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleViewDetail(sale.id)}
                        >
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </CardContent>
      </Card>

      {/* Dialog de detalle */}
      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} maxWidth="md" fullWidth>
        {selectedSale && (
          <>
            <DialogTitle>
              Detalle de Venta - {selectedSale.saleNumber}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Fecha
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedSale.saleDate).toLocaleString('es-AR')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Vendedor
                  </Typography>
                  <Typography variant="body1">
                    {selectedSale.user?.fullName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Cliente
                  </Typography>
                  <Typography variant="body1">
                    {selectedSale.customerName || 'Cliente General'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Documento
                  </Typography>
                  <Typography variant="body1">
                    {selectedSale.customerDocument || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Método de Pago
                  </Typography>
                  <Typography variant="body1">
                    {getPaymentMethodLabel(selectedSale.paymentMethod)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Estado
                  </Typography>
                  <Chip
                    label={selectedSale.paymentStatus}
                    color={getStatusColor(selectedSale.paymentStatus)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" className="mt-md mb-sm">
                    Productos
                  </Typography>
                  <Box className="table-container">
                    <table className="table table-compact">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th className="text-center">Cantidad</th>
                          <th className="text-right">Precio Unit.</th>
                          <th className="text-right">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSale.items?.map((item) => (
                          <tr key={item.id}>
                            <td>{item.productName}</td>
                            <td className="text-center">{item.quantity}</td>
                            <td className="text-right">
                              ${parseFloat(item.unitPrice).toLocaleString('es-AR')}
                            </td>
                            <td className="text-right">
                              ${parseFloat(item.subtotal).toLocaleString('es-AR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="d-flex justify-between mt-md">
                    <Typography variant="h6">Subtotal:</Typography>
                    <Typography variant="h6">
                      ${parseFloat(selectedSale.subtotal).toLocaleString('es-AR')}
                    </Typography>
                  </Box>
                  {selectedSale.discount > 0 && (
                    <Box className="d-flex justify-between">
                      <Typography>Descuento:</Typography>
                      <Typography>
                        -${parseFloat(selectedSale.discount).toLocaleString('es-AR')}
                      </Typography>
                    </Box>
                  )}
                  <Box className="d-flex justify-between mt-sm">
                    <Typography variant="h5" className="font-bold">
                      Total:
                    </Typography>
                    <Typography variant="h5" color="primary" className="font-bold">
                      ${parseFloat(selectedSale.total).toLocaleString('es-AR')}
                    </Typography>
                  </Box>
                </Grid>
                {selectedSale.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Notas
                    </Typography>
                    <Typography variant="body2">
                      {selectedSale.notes}
                    </Typography>
                  </Grid>
                )}

                {/* Sección de Facturación */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" className="mb-md">
                    Facturación Electrónica
                  </Typography>
                  {currentInvoice ? (
                    <Card variant="outlined" sx={{ bgcolor: 'success.lighter', borderColor: 'success.main' }}>
                      <CardContent>
                        <Box className="d-flex align-center gap-xs mb-sm">
                          <CheckCircle color="success" />
                          <Typography variant="subtitle1" fontWeight="600" color="success.dark">
                            Factura Generada
                          </Typography>
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Tipo:
                            </Typography>
                            <Typography variant="body1" fontWeight="600">
                              {currentInvoice.invoice_type}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Número:
                            </Typography>
                            <Typography variant="body1" fontWeight="600">
                              {currentInvoice.invoice_number}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              CAE:
                            </Typography>
                            <Typography 
                              variant="body1" 
                              fontWeight="600"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {currentInvoice.cae}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Vencimiento CAE:
                            </Typography>
                            <Typography variant="body1" fontWeight="600">
                              {new Date(currentInvoice.cae_expiration).toLocaleDateString('es-AR')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ) : selectedSale.paymentStatus === 'pagado' ? (
                    <Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Esta venta aún no tiene una factura electrónica asociada.
                      </Alert>
                      <Button
                        variant="contained"
                        startIcon={<Receipt />}
                        onClick={handleOpenInvoiceModal}
                        fullWidth
                      >
                        Generar Factura Electrónica
                      </Button>
                    </Box>
                  ) : (
                    <Alert severity="warning">
                      Solo se pueden facturar ventas en estado "pagado"
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                startIcon={<Receipt />}
                onClick={() => {
                  setOpenReceipt(true);
                }}
              >
                Ver/Imprimir Recibo
              </Button>
              {isAdmin && selectedSale.paymentStatus !== 'cancelado' && (
                <Button
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => handleCancelSale(selectedSale.id)}
                >
                  Cancelar Venta
                </Button>
              )}
              <Button onClick={() => setOpenDetail(false)}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Modal de facturación */}
      {selectedSale && (
        <InvoiceModal
          open={openInvoiceModal}
          onClose={() => setOpenInvoiceModal(false)}
          sale={selectedSale}
          onSuccess={handleInvoiceSuccess}
        />
      )}

      {/* Modal de recibo */}
      {selectedSale && (
        <ReceiptModal
          open={openReceipt}
          onClose={() => setOpenReceipt(false)}
          sale={selectedSale}
        />
      )}
    </Box>
  );
};

export default Sales;

